import { useState, useEffect } from 'react';
import {
  ArrowLeft, Plus, CalendarDays, MapPin, Package,
  ChevronRight, X,
} from 'lucide-react';

const EventList = ({ authAxios, onBack, onEventClick }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '', city: '', venue: '', start_date: '', end_date: '', event_type: 'exhibition', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await authAxios.get('/api/order-book/events/');
      setEvents(res.data);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.start_date || !formData.end_date) return;
    try {
      setSaving(true);
      await authAxios.post('/api/order-book/events/', formData);
      setShowForm(false);
      setFormData({ name: '', city: '', venue: '', start_date: '', end_date: '', event_type: 'exhibition', notes: '' });
      fetchEvents();
    } catch (err) {
      console.error('Error creating event:', err);
    } finally {
      setSaving(false);
    }
  };

  const statusColors = {
    upcoming: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-gray-100 text-gray-600',
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl active:scale-95 transition-all">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">Exhibitions & Pop-ups</h2>
          <p className="text-gray-500 text-sm font-medium">{events.length} events</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 active:scale-95 transition-all shadow-sm">
          <Plus className="w-4 h-4" /> New Event
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-10 h-10 border-[3px] border-gray-200 border-t-gray-800 rounded-full animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CalendarDays className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-gray-800 font-semibold text-lg">No events yet</p>
          <p className="text-gray-500 text-sm mt-1 max-w-xs mx-auto">Create your first exhibition or pop-up to start tracking orders</p>
          <button onClick={() => setShowForm(true)}
            className="mt-5 px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 active:scale-95 transition-all">
            Create Event
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <button key={event.id} onClick={() => onEventClick(event)}
              className="w-full bg-white rounded-2xl border border-gray-100 p-5 text-left hover:shadow-md active:scale-[0.98] transition-all shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-base font-bold text-gray-900">{event.name}</span>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColors[event.status] || statusColors.completed}`}>
                      {event.status?.charAt(0).toUpperCase() + event.status?.slice(1)}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {event.city && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span>{event.venue ? `${event.venue}, ${event.city}` : event.city}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                      <CalendarDays className="w-4 h-4 flex-shrink-0" />
                      <span>
                        {new Date(event.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        {' — '}
                        {new Date(event.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 mt-1 flex-shrink-0" />
              </div>

              {/* Stats row */}
              <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-1.5">
                  <Package className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-800">{event.order_count || 0}</span>
                  <span className="text-sm text-gray-500">orders</span>
                </div>
                {Number(event.total_pending || 0) > 0 && (
                  <div className="bg-amber-50 px-2.5 py-1 rounded-full">
                    <span className="text-xs font-semibold text-amber-700">₹{Number(event.total_pending).toLocaleString('en-IN')} pending</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create Event Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end justify-center z-50" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up"
            onClick={(e) => e.stopPropagation()}>
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-6 pb-8 pt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">New Event</h3>
                <button onClick={() => setShowForm(false)} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Event Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Dastkar Diwali Mela 2026" required autoFocus
                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ value: 'exhibition', label: 'Exhibition' }, { value: 'popup', label: 'Pop-up' }, { value: 'trunk_show', label: 'Trunk Show' }].map(t => (
                      <button key={t.value} type="button" onClick={() => setFormData({ ...formData, event_type: t.value })}
                        className={`px-3 py-3 rounded-xl text-sm font-semibold border-2 transition-all ${
                          formData.event_type === t.value ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                        }`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                    <input type="text" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g. Delhi"
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Venue</label>
                    <input type="text" value={formData.venue} onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                      placeholder="e.g. Pragati Maidan"
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date *</label>
                    <input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">End Date *</label>
                    <input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required className="w-full px-4 py-3.5 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex gap-3 pt-3">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="flex-1 px-4 py-3.5 border-2 border-gray-200 text-gray-700 rounded-xl text-base font-semibold hover:bg-gray-50 active:scale-95 transition-all">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving}
                    className="flex-1 px-4 py-3.5 bg-gray-900 text-white rounded-xl text-base font-semibold hover:bg-gray-800 disabled:opacity-50 active:scale-95 transition-all">
                    {saving ? 'Creating...' : 'Create Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EventList;
